<template>
  <div
    class="search-bar flex-1"
    @click="clickedOnSearchBar"
  >
    <div class="flex items-start">
      <ais-instant-search
        :search-client="searchClient"
        :index-name="mainSearchIndex"
        class="w-full"
      >
        <ais-configure
          :hits-per-page.camel="ITEM_COUNT_FOR_QUICK_SEARCH"
          :click-analytics.camel="true"
        />
        <ais-autocomplete
          v-slot="{ currentRefinement, indices, refine }"
          class="w-full"
        >
          <div v-click-outside="closeSearchResults">
            <div class="h-11 w-full rounded-md flex justify-center relative">
              <label
                for="search-input"
                class="sr-only"
              >
                {{ $t('Site search') }}
              </label>
              <input
                id="search-input"
                ref="searchInput"
                type="text"
                :value="currentValue.name"
                :maxlength="MAX_LENGTH_FOR_SEARCH_INPUT"
                :placeholder="$t('search-bar.input-placeholder')"
                data-testid="search-bar__input-placeholder"
                class="bg-EXPONDO-white h-full rounded pl-3 pr-20 text-EXPONDO-black w-full relative
                        border border-solid border-EXPONDO-primary-20 placeholder:text-EXPONDO-grey-60"
                @focus="openSearchResults()"
                @input="updateSearch(($event.currentTarget as HTMLInputElement).value, refine)"
                @keyup.enter="onSearch(currentRefinement, refine)"
                @keyup.down="onArrowDown(indices[0].hits)"
                @keyup.up="onArrowUp(indices[0].hits)"
              >
              <button
                v-if="currentValue.name && isSearchResultsOpen"
                data-testid="searchBarClearInput"
                class="right-14 top-3 absolute"
                @click="updateSearch('', refine)"
              >
                <span class="iconify svg--close text-EXPONDO-primary-60 pr-0.5" />
              </button>

              <button
                class="absolute top-0 right-0 w-12 rounded-r h-full flex justify-center items-center"
                data-testid="search-bar__show-results"
                :aria-label="$t('Submit search')"
                @click="onSearch(currentRefinement, refine)"
              >
                <span class="iconify svg--search text-lg text-EXPONDO-primary" />
              </button>
            </div>
            <div
              v-if="currentValue.name && isSearchResultsOpen"
              class="rounded shadow-lg bg-EXPONDO-white relative z-40"
            >
              <template v-if="isSkuSearchActive">
                <div class="p-6 py-4 text-EXPONDO-black">
                  <SearchBarResults
                    v-if="skuResult"
                    data-testid="search-bar__sku-result"
                    :hits="[skuResult]"
                    :active-index="activeIndex"
                    @product-clicked="onSkuResultClicked"
                  />
                  <span
                    v-else
                    data-testid="search-bar__sku-no-result"
                  >{{ $t('No results') }}</span>
                </div>
              </template>
              <template v-else>
                <div
                  v-for="index in indices"
                  :key="index.indexId"
                  class="p-6 py-4 text-EXPONDO-black"
                >
                  <div
                    v-if="index.hits.length > 0"
                  >
                    <SearchBarResults
                      :active-index="activeIndex"
                      @product-clicked="hit => onProductClicked(hit, currentRefinement)"
                      @search-results-shown="onSearchResultsShown(index.hits)"
                    />
                    <button
                      v-if="index.hits.length >= 5"
                      class="text-EXPONDO-primary mt-2 flex items-center mx-auto"
                      @click="onSearch(currentRefinement, refine)"
                    >
                      {{ $t('See all results') }}
                      <span class="iconify svg--chevron-right text-EXPONDO-primary text-xs ml-4" />
                    </button>
                  </div>
                  <span v-else>{{ $t('No results') }}</span>
                </div>
              </template>
            </div>
          </div>
        </ais-autocomplete>
      </ais-instant-search>
    </div>
  </div>
</template>

<script async setup lang="ts">

import {
  AisAutocomplete,
  AisConfigure,
  AisInstantSearch, createServerRootMixin,
} from 'vue-instantsearch/vue3/es';
import type { HitResultItem } from 'vue-instantsearch/vue3/es';
import { getTypesenseClient } from '~~/helpers/typesense/typesense';
import {
  ITEM_COUNT_FOR_QUICK_SEARCH,
  MIN_CHARS_FOR_SEARCH,
  MAX_LENGTH_FOR_SEARCH_INPUT,
} from '~~/constants/typesense';
import { ROUTES } from '~~/constants/routes';
import { isSkuSearch } from '~~/helpers/search/isSkuSearch';

interface Props {
  languageAndCountry: string,
  mainSearchIndex: string,
}

const props = defineProps<Props>();
const { languageAndCountry, mainSearchIndex } = toRefs(props);
const emit = defineEmits<{
    (e: 'product-clicked', payload: { product: HitResultItem; searchTerm: string }): void;
    (e: 'search', searchTerm: string): void;
    (e: 'search-bar-clicked'): void;
    (e: 'search-results-shown', products: HitResultItem[]): void;
  }>();

const { getAdjustedSlug } = useRouteExtended();
const router = useRouter();
const searchInput = ref<HTMLElement | null>(null);

const { skuResult, search: searchBySku, clear: clearSkuResult } = useSkuSearch();
const isSkuSearchActive = ref<boolean>(false);

const DEFAULT_ACTIVE_INDEX: number = -1;
const activeIndex = ref<number>(DEFAULT_ACTIVE_INDEX);
const currentValue = ref<{ name: string; url: string; }>({ name: '', url: '' });
const isSearchResultsOpen = ref<boolean>(false);
const updateSearch = (value: string, refine: (value: string) => void) => {

  if (!value) {

    currentValue.value = { name: '', url: '' };
    refine('');
    closeSearchResults();
    clearSkuResult();
    isSkuSearchActive.value = false;

    if (searchInput.value instanceof HTMLElement) {

      searchInput.value.focus();

    }

    return;

  }

  if (isSkuSearch(value)) {

    currentValue.value = { name: value, url: '' };
    isSkuSearchActive.value = true;
    openSearchResults();
    searchBySku(value);

    return;

  }

  isSkuSearchActive.value = false;
  clearSkuResult();

  if (value.length < MIN_CHARS_FOR_SEARCH) {

    return;

  }

  refine(value);
  openSearchResults();
  currentValue.value = { name: value, url: '' };

};

const onSearch = (currentRefinement: string, refineCallback?: (value: string) => void) => {

  if (isSkuSearchActive.value) {

    closeSearchResults();

    if (skuResult.value) {

      router.push(skuResult.value.url);

      return;

    }

    const searchTerm = currentValue.value.name?.trim() || '';
    const queryParams: Record<string, string> = { query: searchTerm };

    router.push({ path: getAdjustedSlug(ROUTES.SEARCH), query: queryParams });

    return;

  }

  const trimmedRefinement = currentRefinement?.trim?.() || '';
  const currentName = currentValue.value.name?.trim?.() || '';
  const searchTerm = currentName || trimmedRefinement;

  if (!searchTerm && typeof refineCallback === 'function') {

    refineCallback('');

  }

  emit('search', searchTerm);

  closeSearchResults();

  if (
    currentValue.value.url &&
        currentName &&
        currentName === trimmedRefinement
  ) {

    router.push(currentValue.value.url);

    return;

  }

  const hitsPerPageParam = router.currentRoute.value.query?.hitsPerPage;
  const hitsPerPage = Array.isArray(hitsPerPageParam)
    ? hitsPerPageParam[0]
    : hitsPerPageParam ?? '';

  const queryParams: Record<string, string> = {};

  if (typeof hitsPerPage === 'string' && hitsPerPage.length) {

    queryParams.hitsPerPage = hitsPerPage;

  }

  queryParams.query = searchTerm || '';
  router.push({
    path: getAdjustedSlug(ROUTES.SEARCH),
    query: queryParams,
  });

};

const setCurrentValue = (value: HitResultItem) => {

  currentValue.value = { name: value.name || '', url: value.url || '' };

};

const onArrowUp = (hits: HitResultItem[]) => {

  if (activeIndex.value < 1) {

    activeIndex.value = hits.length - 1;

  } else {

    activeIndex.value--;
    setCurrentValue(hits[activeIndex.value]);

  }

};

const onArrowDown = (hits: HitResultItem[]) => {

  if (activeIndex.value + 1 < hits.length) {

    activeIndex.value++;
    setCurrentValue(hits[activeIndex.value]);

  } else {

    activeIndex.value = 0;

  }

};

const openSearchResults = () => {

  isSearchResultsOpen.value = true;

};

const closeSearchResults = () => {

  isSearchResultsOpen.value = false;

};

const onProductClicked = (product: HitResultItem, searchTerm: string) => {

  closeSearchResults();
  emit('product-clicked', { product, searchTerm });

};

const onSkuResultClicked = () => {

  closeSearchResults();

};

const onSearchResultsShown = (products: HitResultItem[]) => {

  emit('search-results-shown', products);

};

setCurrentValue({ name: '', url: '' });

const searchClient = await getTypesenseClient(languageAndCountry.value);
const serverRootMixin = ref(
  createServerRootMixin({
    searchClient,
    indexName: mainSearchIndex.value,
  }),
);
const { instantsearch } = serverRootMixin.value.data();

provide('$_ais_InstantSearchInstance', instantsearch);

const clickedOnSearchBar = () => {

  emit('search-bar-clicked');

};
</script>
